import { indentUnit, LanguageDescription, LanguageSupport, StreamLanguage, syntaxHighlighting, type StreamParser } from "@codemirror/language";
import type { Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { tagHighlighter, tags } from "@lezer/highlight";

// Highlighting for the code that sits beside a thesis: analysis scripts, data
// and config. Each mode is its own chunk, fetched the first time a file needs
// it. Anything not listed here keeps the LaTeX mode.

type Mode = Promise<StreamParser<unknown>>;

function lang(name: string, extensions: string[], load: () => Mode, opts: { indent?: string; filename?: RegExp } = {}) {
  return LanguageDescription.of({
    name,
    extensions,
    filename: opts.filename,
    load: async () => new LanguageSupport(StreamLanguage.define(await load()), opts.indent ? indentUnit.of(opts.indent) : []),
  });
}

const FOUR = { indent: "    " };

const languages = [
  lang("Python", ["py", "pyw", "pyi"], () => import("@codemirror/legacy-modes/mode/python").then((m) => m.python), FOUR),
  lang("JavaScript", ["js", "mjs", "cjs", "jsx"], () => import("@codemirror/legacy-modes/mode/javascript").then((m) => m.javascript)),
  lang("TypeScript", ["ts", "mts", "cts", "tsx"], () => import("@codemirror/legacy-modes/mode/javascript").then((m) => m.typescript)),
  lang("JSON", ["json", "jsonc", "ipynb"], () => import("@codemirror/legacy-modes/mode/javascript").then((m) => m.json)),
  lang("C", ["c", "h"], () => import("@codemirror/legacy-modes/mode/clike").then((m) => m.c), FOUR),
  lang("C++", ["cpp", "cc", "cxx", "hpp", "hh", "hxx", "ino"], () => import("@codemirror/legacy-modes/mode/clike").then((m) => m.cpp), FOUR),
  lang("Java", ["java"], () => import("@codemirror/legacy-modes/mode/clike").then((m) => m.java), FOUR),
  lang("C#", ["cs"], () => import("@codemirror/legacy-modes/mode/clike").then((m) => m.csharp), FOUR),
  lang("Kotlin", ["kt", "kts"], () => import("@codemirror/legacy-modes/mode/clike").then((m) => m.kotlin), FOUR),
  lang("MATLAB", ["m"], () => import("@codemirror/legacy-modes/mode/octave").then((m) => m.octave), FOUR),
  lang("R", ["r"], () => import("@codemirror/legacy-modes/mode/r").then((m) => m.r)),
  lang("Julia", ["jl"], () => import("@codemirror/legacy-modes/mode/julia").then((m) => m.julia), FOUR),
  lang("Shell", ["sh", "bash", "zsh", "env"], () => import("@codemirror/legacy-modes/mode/shell").then((m) => m.shell)),
  lang("Lua", ["lua"], () => import("@codemirror/legacy-modes/mode/lua").then((m) => m.lua)),
  // latexmk's config files are Perl.
  lang("Perl", ["pl", "pm"], () => import("@codemirror/legacy-modes/mode/perl").then((m) => m.perl), { ...FOUR, filename: /^\.?latexmkrc$/ }),
  lang("Ruby", ["rb"], () => import("@codemirror/legacy-modes/mode/ruby").then((m) => m.ruby)),
  lang("Go", ["go"], () => import("@codemirror/legacy-modes/mode/go").then((m) => m.go), { indent: "\t" }),
  lang("Rust", ["rs"], () => import("@codemirror/legacy-modes/mode/rust").then((m) => m.rust), FOUR),
  lang("Swift", ["swift"], () => import("@codemirror/legacy-modes/mode/swift").then((m) => m.swift), FOUR),
  lang("Haskell", ["hs"], () => import("@codemirror/legacy-modes/mode/haskell").then((m) => m.haskell)),
  lang("Fortran", ["f", "for", "f90", "f95", "f03", "f08"], () => import("@codemirror/legacy-modes/mode/fortran").then((m) => m.fortran)),
  lang("Verilog", ["v", "sv", "svh"], () => import("@codemirror/legacy-modes/mode/verilog").then((m) => m.verilog)),
  lang("VHDL", ["vhd", "vhdl"], () => import("@codemirror/legacy-modes/mode/vhdl").then((m) => m.vhdl)),
  lang("SQL", ["sql"], () => import("@codemirror/legacy-modes/mode/sql").then((m) => m.standardSQL)),
  lang("HTML", ["html", "htm"], () => import("@codemirror/legacy-modes/mode/xml").then((m) => m.html)),
  lang("XML", ["xml", "xsl", "xsd", "plist"], () => import("@codemirror/legacy-modes/mode/xml").then((m) => m.xml)),
  lang("CSS", ["css"], () => import("@codemirror/legacy-modes/mode/css").then((m) => m.css)),
  lang("SCSS", ["scss"], () => import("@codemirror/legacy-modes/mode/css").then((m) => m.sCSS)),
  // CITATION.cff is YAML.
  lang("YAML", ["yaml", "yml", "cff"], () => import("@codemirror/legacy-modes/mode/yaml").then((m) => m.yaml)),
  lang("TOML", ["toml"], () => import("@codemirror/legacy-modes/mode/toml").then((m) => m.toml)),
  lang("INI", ["ini", "properties"], () => import("@codemirror/legacy-modes/mode/properties").then((m) => m.properties)),
  lang("Dockerfile", ["dockerfile"], () => import("@codemirror/legacy-modes/mode/dockerfile").then((m) => m.dockerFile), { filename: /^dockerfile$/ }),
  lang("CMake", ["cmake"], () => import("@codemirror/legacy-modes/mode/cmake").then((m) => m.cmake), { filename: /^cmakelists\.txt$/ }),
  lang("Diff", ["diff", "patch"], () => import("@codemirror/legacy-modes/mode/diff").then((m) => m.diff)),
];

/** The code language for a project path, or null for LaTeX and plain text. */
export function codeLanguage(path: string): LanguageDescription | null {
  return LanguageDescription.matchFilename(languages, path.slice(path.lastIndexOf("/") + 1).toLowerCase());
}

// classHighlighter files builtins (print, len) under plain tok-variableName.
const builtins = tagHighlighter([{ tag: tags.standard(tags.variableName), class: "tok-builtin" }]);

/** A code file's language (null while it loads) with the code colours, which live under .cm-code in styles.css. */
export function codeHighlighting(support: LanguageSupport | null): Extension {
  return [support ?? [], syntaxHighlighting(builtins), EditorView.editorAttributes.of({ class: "cm-code" })];
}
